import { AfterViewInit, Component, computed, inject, signal, TemplateRef, viewChild } from "@angular/core";
import { RouterLink } from "@angular/router";
import { PatternPipe, PHONE_LIKE_PATTERN } from "@kno2/common/pipes";
import { createRefreshScheduler } from "@kno2/core/browser";
import { SessionService } from "@kno2/core/session";
import { SettingsService } from "@kno2/data-access/settings";
import { UserEventsQuery, UserMessageEvent, UsersService } from "@kno2/data-access/users";
import { PaginationComponent } from "@kno2/shared/pagination";
import { TableColumn, TableComponent, TableFilterEvent } from "@kno2/shared/table";
import { filterNonEmptyProperties, parseUtcDate } from "@kno2/utils";
import { format, secondsToMilliseconds } from "date-fns";
import { FilterMatchMode } from "primeng/api";
import { ReleaseStatusCellContentComponent } from "./release-status-cell-content/release-status-cell-content.component";

@Component({
    selector: "kno2-release-history",
    imports: [PaginationComponent, TableComponent, RouterLink, ReleaseStatusCellContentComponent],
    providers: [PatternPipe],
    templateUrl: "./release-history.component.html",
    styles: ``
})
export class ReleaseHistoryComponent implements AfterViewInit {
    private readonly sessionService = inject(SessionService);
    private readonly usersService = inject(UsersService);
    private readonly settingsService = inject(SettingsService);
    private readonly patternPipe = inject(PatternPipe);

    private readonly refreshScheduler = createRefreshScheduler(secondsToMilliseconds(15), () => this.pageOptions.update((options) => ({ ...options })));

    protected statusTemplate = viewChild<TemplateRef<{ event: UserMessageEvent }>>("status");
    protected subjectTemplate = viewChild<TemplateRef<{ event: UserMessageEvent }>>("subject");
    protected toAddressTemplate = viewChild<TemplateRef<{ event: UserMessageEvent }>>("toAddress");
    protected fromAddressTemplate = viewChild<TemplateRef<{ event: UserMessageEvent }>>("fromAddress");

    protected interoperabilitySettings = this.settingsService.getInteroperabilitySettings();
    protected deliverySummaryEnableDownload = computed(() => this.interoperabilitySettings()?.deliverySummaryEnableDownload);
    protected pageOptions = signal<UserEventsQuery>({
        pageStart: 1,
        pageSize: 10
    });
    protected events = this.usersService.getEvents(this.sessionService.getProfile().userId, this.pageOptions);
    protected columns = signal<TableColumn<UserMessageEvent>[]>([]);

    public ngAfterViewInit(): void {
        this.columns.set(this.getColumns());
    }

    public updateEventsQuery(query?: UserEventsQuery): void {
        this.pageOptions.update((options) => ({
            ...options,
            ...(query || {})
        }));
        this.refreshScheduler.reset();
    }

    public filterEvents(event: TableFilterEvent): void {
        const filterEntries = Object.entries(event.filters || {});
        const [prop, filters] = filterEntries[0] || [];
        let search = filters?.[0]?.value;

        if (search) {
            const isAddressColumn = prop === "to" || prop === "from";
            if (isAddressColumn && this.patternPipe.transform(search, PHONE_LIKE_PATTERN)) {
                search = search.replace(/\D/g, "");
            }
        }

        this.pageOptions.update(({ pageStart, pageSize }) =>
            filterNonEmptyProperties({
                pageStart,
                pageSize,
                prop,
                search: prop === "date" ? new Date(search).toISOString() : search
            })
        );
        this.refreshScheduler.reset();
    }

    private getColumns(): TableColumn<UserMessageEvent>[] {
        return [
            {
                field: "status",
                templateRef: this.statusTemplate(),
                width: "17%",
                cellClass: "overflow-visible pb-2",
                filter: {
                    type: "select",
                    options: [
                        "Bounced",
                        "Deleted",
                        "Delivered",
                        "Exported",
                        "Failed",
                        "Faxed",
                        "Forwarded",
                        "Forwarding",
                        "Pending",
                        "Processed",
                        "Read",
                        "Received",
                        "Replied",
                        "Replying",
                        "Sending",
                        "Sent",
                        "Unknown"
                    ].map((status) => ({ label: status, value: status }))
                }
            },
            {
                field: "messageSentDate",
                headerText: "Sent",
                formatter: ({ messageSentDate }) => format(parseUtcDate(messageSentDate), "h:mm a MMM d, y"),
                width: "15%",
                filter: { type: "date", prop: "date" }
            },
            {
                field: "subject",
                templateRef: this.subjectTemplate(),
                width: "17%"
            },
            {
                field: "to",
                cellClass: "overflow-visible k2-ellipsis",
                width: "25%",
                filter: { type: "text", matchMode: FilterMatchMode.CONTAINS },
                templateRef: this.toAddressTemplate()
            },
            {
                field: "from",
                cellClass: "overflow-visible",
                filter: { type: "text", matchMode: FilterMatchMode.CONTAINS },
                templateRef: this.fromAddressTemplate()
            }
        ];
    }
}
